import type React from 'react';
import { type ChangeEvent, type DragEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { FileTextIcon } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/spinner';
import { getDocumentOptions as getDocumentEstateOptions } from '@/features/estate-document/api/document';
import {
  useUploadDocument as useUploadEstateDocument,
  useUploadProjectDocument,
} from '@/features/estate-document/api/upload-document';
import type { Estate } from '@/features/estates-property/api/estate';
import { getDocumentOptions as getProjectDocumentOptions } from '@/features/project-document/api/document';
import type { Project } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';

import { ACCEPTED_INPUT_TYPE, uploadDocumentSchema } from '../schema/document-schema';

type UploadDocumentType = {
  files: File[];
};

export interface UploadDocumentsModalProps {
  id: Estate['id'] | Project['id'];
  order: 'asc' | 'desc';
  type?: 'estate' | 'project';
}

export const UploadDocumentsModal: React.FC<UploadDocumentsModalProps> = ({
  id,
  order,
  type = 'estate',
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UploadDocumentType>({
    resolver: zodResolver(uploadDocumentSchema()),
    defaultValues: {
      files: [],
    },
  });

  const files = watch('files');

  const queryClient = useQueryClient();

  const { mutateAsync: uploadEstateDocument } = useUploadEstateDocument({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getDocumentEstateOptions(id, order).queryKey }),
    },
  });

  const { mutateAsync: uploadProjectDocument } = useUploadProjectDocument({
    mutationConfig: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getProjectDocumentOptions(id, order).queryKey }),
    },
  });

  const [, setDragActive] = useState(false);

  const fileInputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);
    reset({ files: [...files, ...newFiles] });
  };

  const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const newFiles = Array.from(event.dataTransfer.files || []);
    reset({ files: [...files, ...newFiles] });
  };

  const removeFile = (index: number) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    reset({ files: updatedFiles });
  };

  const submitHandler = async (data: UploadDocumentType) => {
    const promises: Promise<void>[] = [];
    data.files.forEach((file) => {
      if (type === 'project') {
        promises.push(uploadProjectDocument({ document: file, name: file.name, projectId: id }));
      } else {
        promises.push(uploadEstateDocument({ document: file, name: file.name, estateId: id }));
      }
    });
    await Promise.all(promises);
    closeModal();
  };

  return (
    <ModalBase className="max-w-96">
      {isSubmitting ? (
        <div className="absolute inset-0 flex items-center justify-center bg-primary-lighter/40">
          <Spinner className="size-12 [&_*]:stroke-primary-dark" />
        </div>
      ) : null}
      <form onSubmit={handleSubmit(submitHandler)}>
        <button
          type="button"
          className="flex w-full flex-col items-center justify-center space-y-3"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <label
            htmlFor="dropzone-file"
            className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-text-secondary p-4 hover:bg-secondary"
          >
            <div className="flex flex-col items-center justify-center pb-6 pt-5">
              <svg
                className="mb-4 size-8 text-text-secondary"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="mb-1 text-center text-sm text-text-secondary">
                {t('uploadDocumentText')}
              </p>
              <p className={cn('text-sm text-text-secondary', errors.files && 'text-danger')}>
                {t('pdf')}
              </p>
            </div>
            <Input
              id="dropzone-file"
              type="file"
              accept={`.${ACCEPTED_INPUT_TYPE}`}
              className="hidden"
              disabled={isSubmitting}
              multiple
              {...register('files', { onChange: fileInputChangeHandler })}
            />
          </label>
        </button>
        {files && files.length > 0 ? (
          <div className="grid max-h-96 grid-cols-3 gap-2 overflow-y-scroll">
            {files?.map((_, index) => (
              <div key={`File${String.fromCharCode(65 + index)}`} className="relative h-28">
                <div className="flex size-full items-center justify-center text-primary-light">
                  <FileTextIcon size={80} />
                </div>
                <button
                  type="button"
                  className="absolute right-0 top-0 rounded-full text-text-secondary"
                  onClick={() => removeFile(index)}
                  disabled={isSubmitting}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <Button
            onClick={() => {
              closeModal();
            }}
            type="button"
            variant="outline"
            className="border-red-500 text-red-500"
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {t('submit')}
          </Button>
        </div>
      </form>
    </ModalBase>
  );
};
